import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, Award, CheckCircle2, Trophy } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EndorsementForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    endorser_name: "",
    endorser_email: "",
    endorser_role: "",
    endorser_organization: "",
    endorser_is_hof: false,
    endorser_is_national_champion: false,
    relationship: "",
    trait_ratings: {},
    narrative: "",
  });

  useEffect(() => {
    axios.get(`${API}/endorsements/${token}`)
      .then(({ data }) => {
        setData(data);
        setForm((f) => ({
          ...f,
          endorser_name: data.endorser_name || "",
          relationship: data.relationship || "",
        }));
      })
      .catch(() => toast.error("This endorsement link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const setRating = (trait, n) => setForm({ ...form, trait_ratings: { ...form.trait_ratings, [trait]: n } });

  const submit = async () => {
    if (!form.endorser_name || !form.relationship || !form.narrative) {
      return toast.warning("Please fill in your name, relationship, and a brief narrative.");
    }
    setBusy(true);
    try {
      await axios.post(`${API}/endorsements/${token}/submit`, form);
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-appbg text-white flex items-center justify-center heading text-2xl">LOADING…</div>;
  if (!data) return <div className="min-h-screen bg-appbg text-white flex items-center justify-center text-neutral-400">Endorsement link not found.</div>;

  if (done || data.status === "approved") {
    return (
      <div className="min-h-screen bg-appbg text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="endorsement-thanks">
          <CheckCircle2 className="text-volt mx-auto" size={48} />
          <h1 className="heading text-4xl mt-4">THANK YOU</h1>
          <p className="text-neutral-300 mt-4">Your endorsement for <b className="text-white">{data.coach?.name}</b> has been recorded. It will appear in their vetting report immediately and may contribute to their Coach Impact Rating (CIR™).</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg text-white">
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="endorsement-form">
        <div className="text-xs tracking-widest text-blaze">COACHCONNECT AI · PEER ENDORSEMENT</div>
        <h1 className="heading text-4xl md:text-5xl mt-2 leading-none">ENDORSE <span className="text-blaze">{data.coach?.name?.toUpperCase()}</span></h1>
        <p className="text-neutral-400 mt-3 max-w-2xl">
          {data.coach?.name} has asked you to vouch for them on CoachConnect AI. Your honest assessment helps athletic
          directors and program admins make better hiring decisions. <b className="text-volt">Endorsements from HOF coaches
          and national-champion-winning coaches carry meaningful weight in their Coach Impact Rating (CIR™).</b>
        </p>

        <Section title="ABOUT YOU">
          <Grid>
            <Field label="Your Full Name *"><input className="input" data-testid="endorser-name" value={form.endorser_name} onChange={(e) => setForm({ ...form, endorser_name: e.target.value })} /></Field>
            <Field label="Email (optional)"><input className="input" type="email" data-testid="endorser-email" value={form.endorser_email} onChange={(e) => setForm({ ...form, endorser_email: e.target.value })} /></Field>
            <Field label="Your Role / Title"><input className="input" data-testid="endorser-role" placeholder="e.g. Head Coach, Athletic Director, Player" value={form.endorser_role} onChange={(e) => setForm({ ...form, endorser_role: e.target.value })} /></Field>
            <Field label="Program / Organization"><input className="input" data-testid="endorser-org" placeholder="e.g. Loyola Maryland MLAX" value={form.endorser_organization} onChange={(e) => setForm({ ...form, endorser_organization: e.target.value })} /></Field>
            <Field label={`Your relationship to ${data.coach?.name} *`}><input className="input" data-testid="endorser-relationship" placeholder="e.g. coached me at Duke 2018-2022" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} /></Field>
          </Grid>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <CredToggle on={form.endorser_is_hof} onChange={(v) => setForm({ ...form, endorser_is_hof: v })} testid="endorser-hof"
              icon={<Trophy size={16} />} label="I am in a coaching Hall of Fame"
              sub="USILA · NILCA · IMLCA · IWLCA · State HOF · etc." />
            <CredToggle on={form.endorser_is_national_champion} onChange={(v) => setForm({ ...form, endorser_is_national_champion: v })} testid="endorser-nat-champ"
              icon={<Award size={16} />} label="I have won a national championship as a coach"
              sub="Adds meaningful weight to your endorsement" />
          </div>
        </Section>

        <Section title="RATE THE COACH (1 = NEEDS WORK · 5 = EXCEPTIONAL)">
          <p className="text-xs text-neutral-500 -mt-2">Skip any trait you can&apos;t honestly speak to. Partial ratings are still useful.</p>
          <div className="space-y-2" data-testid="trait-ratings">
            {data.traits.map((t, i) => (
              <div key={i} className="border border-neutral-900 p-3 bg-appcard">
                <div className="text-sm text-neutral-200">{t}</div>
                <div className="flex gap-1.5 mt-2" data-testid={`trait-row-${i}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`trait-${i}-rating-${n}`}
                      onClick={() => setRating(t, n)}
                      className={`flex-1 py-2 text-xs heading transition-colors ${form.trait_ratings[t] === n ? "bg-volt text-white" : "border border-neutral-800 text-neutral-400 hover:border-volt"}`}
                    >
                      <Star size={14} className="inline" /> {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="NARRATIVE *">
          <Field label={`Tell us how ${data.coach?.name} stands out — be specific. 1-3 paragraphs.`}>
            <textarea className="input min-h-[120px]" data-testid="endorser-narrative" value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} placeholder="A specific story or two beats a list of adjectives. What did this coach DO that mattered?" />
          </Field>
        </Section>

        <button
          onClick={submit}
          disabled={busy}
          data-testid="endorsement-submit-btn"
          className="w-full mt-8 bg-blaze text-white heading text-xl py-4 min-h-[56px] hover:glow-blaze transition-all disabled:opacity-50"
        >
          {busy ? "SUBMITTING…" : "SUBMIT ENDORSEMENT"}
        </button>
        <p className="text-xs text-neutral-500 mt-3 text-center">By submitting you confirm this endorsement is honest and based on direct knowledge of the coach.</p>
      </div>
      <style>{`.input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 10px 12px; color: white; outline: none; font-size: 13px; } .input:focus { border-color: #FF3B30; }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 border border-neutral-800 p-6 bg-appcard">
      <div className="text-xs tracking-widest text-volt mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>; }
function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>{children}</label>;
}
function CredToggle({ on, onChange, testid, icon, label, sub }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!on)} className={`text-left p-4 border transition-colors ${on ? "border-volt bg-volt/10" : "border-neutral-800 hover:border-neutral-600"}`}>
      <div className={`flex items-center gap-2 text-sm heading ${on ? "text-volt" : "text-neutral-300"}`}>{icon} {label}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
      <div className={`text-[10px] tracking-widest mt-2 ${on ? "text-volt" : "text-neutral-600"}`}>{on ? "✓ CONFIRMED" : "TAP TO CONFIRM"}</div>
    </button>
  );
}
